import {Phone, Calendar, Shield, Award, Check, MapPin, Clock, MessageCircleDashed as MessageCircle, Car} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'
import CanonicalTag from '@/components/CanonicalTag'

const corvetteServiceOptions = [
  { value: "front-bumper-ppf", label: "Front Bumper PPF" },
  { value: "full-front-ppf", label: "Full Front PPF" },
  { value: "track-pack", label: "Track Pack" },
  { value: "full-body-gloss-ppf", label: "Full Body Gloss PPF" },
  { value: "matte-stealth-ppf", label: "Matte / Stealth PPF" },
  { value: "colored-ppf", label: "Colored PPF" },
  { value: "other", label: "Not Sure / Recommend Coverage" },
]

const faqs = [
  {
    question: "Is PPF worth it on a C8 Corvette?",
    answer: "For many C8 owners, yes — especially if the car sees highway miles, canyon roads, or track days. The low front bumper, wide body, and leading edges of the hood and fenders take debris first. Paint protection film is a sacrificial layer over those painted surfaces. It is not a substitute for careful driving, and it will not make a Corvette chip-proof. We quote coverage based on how you actually use the car."
  },
  {
    question: "How much does Corvette PPF cost?",
    answer: "Corvette PPF starting prices at Mr Wrap: front bumper from $799+, partial front from $1,499+, full front from $1,999+, track package from $2,999+, full body gloss from $5,499+, matte/stealth from $6,499+, and colored PPF from $6,999+. These are starting prices, not locked Corvette quotes. Final cost depends on generation (C8, C7, and others), coverage, film, aero or carbon pieces, paint condition, and installation complexity. Call, text, or send the quote form for a number on your car."
  },
  {
    question: "Should I PPF my entire Corvette or just the front?",
    answer: "Full-front PPF is the most common starting point because the bumper, hood, fenders, and mirrors collect the most debris. A track-style package adds lower rockers and other high-impact areas when you want more than the front without wrapping every panel. Full-body gloss, matte/stealth, or colored PPF makes sense if you want every painted surface covered or you plan to keep the car long term. We help you choose after we know the model and how you drive."
  },
  {
    question: "What areas of a C8 get the most rock chips?",
    answer: "On a C8, the front bumper, leading edge of the hood, fenders, mirrors, and lower rocker area typically see the most chips from freeway and canyon driving. Rear valance and diffuser areas can also take debris depending on ride height and aero. Not every C8 is equipped the same, so we inspect the car before recommending extra pieces."
  },
  {
    question: "Can you PPF a Corvette Z06?",
    answer: "Yes. We install paint protection film on Corvette Z06 models as well as Stingray, E-Ray, C7, and other Corvette generations. Z06 coverage is quoted individually because the wider body, front end, and any carbon or aero pieces change the amount of film and time required."
  },
  {
    question: "Can carbon fiber/aero pieces be protected?",
    answer: "Often yes, when the surface is a good candidate for film. Painted aero, some carbon-look pieces, and add-on splitters or rocker extensions are reviewed in person. We do not treat every Z06 or every aero package the same, and we will tell you if a piece should be left uncovered."
  },
  {
    question: "Can PPF be installed on a brand-new Corvette?",
    answer: "Yes. Many owners film a new Corvette before putting highway miles on it. The car still needs to be washed, inspected, and prepped. If the paint has transport marks or light defects that would show under film, we discuss that before install."
  },
  {
    question: "How long does Corvette PPF last?",
    answer: "Premium PPF is designed as a long-term film. How long it looks its best depends on the film selected, sun and wash habits, and whether the car is a weekend car or a daily driver. Warranty coverage follows the film manufacturer for the product we install, plus our installation workmanship. We confirm those terms with your quote."
  },
  {
    question: "Can I ceramic coat over PPF?",
    answer: "Yes. A ceramic coating can be applied over cured PPF to help with water behavior and easier washing. Coating is optional. Some Corvette owners also add ceramic window tint in the same Vista shop. Tell us if you want film only, or film plus tint or coating, so we plan the schedule correctly."
  },
  {
    question: "Where is Mr Wrap located?",
    answer: "Mr Wrap is at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084. Phone 858-282-8119. We install Corvette PPF at the Vista shop for owners throughout North County and San Diego County, including Oceanside, Carlsbad, San Marcos, Encinitas, Rancho Santa Fe, and greater San Diego."
  }
]

const CorvettePpfSanDiegoPage = () => {
  const navigate = useNavigate()

  const goToQuote = () => {
    const el = document.getElementById("corvette-ppf-quote")
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }
    navigate("/contact")
    setTimeout(() => window.scrollTo(0, 0), 100)
  }

  useEffect(() => {
    const faqScript = document.createElement('script')
    faqScript.id = 'corvette-ppf-faq-schema'
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    })
    document.head.appendChild(faqScript)

    const serviceScript = document.createElement('script')
    serviceScript.id = 'corvette-ppf-service-schema'
    serviceScript.type = 'application/ld+json'
    serviceScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Corvette Paint Protection Film San Diego",
      "serviceType": "Corvette Paint Protection Film (PPF)",
      "description": "Corvette paint protection film installation in Vista, serving North County and San Diego. Full front, track-style, full body, matte/stealth, and colored PPF for C8 Stingray, Z06, E-Ray, C7, and other Corvette models.",
      "url": "https://autowrapsandiego.com/corvette-ppf-san-diego",
      "areaServed": ["San Diego", "Vista", "Oceanside", "Carlsbad", "San Marcos", "Encinitas", "Rancho Santa Fe", "North County San Diego", "San Diego County"],
      "provider": {
        "@type": "LocalBusiness",
        "name": "Mr Wrap",
        "telephone": "+1-858-282-8119",
        "email": "mrwrapmatt@gmail.com",
        "url": "https://autowrapsandiego.com/",
        "image": "https://static.lumi.new/71/7132523da35951c352e5739adcc9dffa.webp",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2244 South Santa Fe Avenue, Suite A7",
          "addressLocality": "Vista",
          "addressRegion": "CA",
          "postalCode": "92084",
          "addressCountry": "US"
        }
      }
    })
    document.head.appendChild(serviceScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.id = 'corvette-ppf-breadcrumb-schema'
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://autowrapsandiego.com/" },
        { "@type": "ListItem", "position": 2, "name": "Paint Protection Film San Diego", "item": "https://autowrapsandiego.com/paint-protection-film-san-diego" },
        { "@type": "ListItem", "position": 3, "name": "Corvette PPF San Diego", "item": "https://autowrapsandiego.com/corvette-ppf-san-diego" }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    return () => {
      faqScript.remove()
      serviceScript.remove()
      breadcrumbScript.remove()
    }
  }, [])

  return (
    <>
      <CanonicalTag url="/corvette-ppf-san-diego" />
      <Helmet>
        <title>Corvette PPF San Diego | C8 & Z06 Paint Protection | Mr Wrap</title>
        <meta
          name="description"
          content="Corvette PPF in Vista, North County San Diego. 13 years of professional installation. Full front, track-style, full body, and colored PPF for C8, Z06, E-Ray, and C7. Call or text 858-282-8119."
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="Corvette PPF San Diego | C8 & Z06 Paint Protection | Mr Wrap" />
        <meta
          property="og:description"
          content="Professional Corvette paint protection film at Mr Wrap in Vista. C8 Stingray, Z06, E-Ray, and C7 coverage with published starting prices. Call or text 858-282-8119."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://autowrapsandiego.com/corvette-ppf-san-diego" />
        <meta property="og:image" content="https://static.lumi.new/8d/8d4348c7cc773738b6d1e2ff7c726e8c.webp" />
      </Helmet>

      <div className="pt-20 min-h-screen bg-black pb-40 md:pb-0">
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-12 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <figure className="mb-8 overflow-hidden rounded-xl border border-cyan-500/20 bg-gray-900">
              <img
                src="https://static.lumi.new/8d/8d4348c7cc773738b6d1e2ff7c726e8c.webp"
                alt="C8 Corvettes outside Mr Wrap in Vista California"
                width={1600}
                height={1000}
                sizes="(max-width: 768px) 100vw, 1280px"
                className="w-full h-auto max-h-[280px] sm:max-h-[420px] lg:max-h-[520px] object-cover object-center"
                fetchPriority="high"
                decoding="async"
              />
              <figcaption className="px-4 py-3 text-sm text-gray-400">
                Real Corvettes at our Vista shop — Mr Wrap on South Santa Fe Avenue.
              </figcaption>
            </figure>
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-cyan-400 font-semibold mb-3">Mr Wrap · Vista / North County San Diego</p>
                <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">Corvette PPF San Diego</h1>
                <p className="text-lg sm:text-xl text-gray-300 mb-4">
                  Professional Paint Protection Film for C8 Stingray, Z06, E-Ray, C7 & Corvette Models
                </p>
                <p className="text-lg text-gray-300 mb-8">
                  13 years of professional installation experience. Indoor installs at our Vista shop. Tell us the year and model for a clear Corvette quote.
                </p>
                <div className="flex gap-3 justify-start flex-wrap">
                  <a
                    href="tel:858-282-8119"
                    className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-4 rounded-lg font-bold transition text-lg w-full sm:w-auto"
                  >
                    <Phone size={20} />
                    CALL / TEXT 858-282-8119
                  </a>
                  <a
                    href="sms:858-282-8119"
                    className="flex items-center justify-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 border-2 border-cyan-500 text-cyan-400 px-6 py-4 rounded-lg font-semibold transition w-full sm:w-auto"
                  >
                    <MessageCircle size={18} />
                    Text Us
                  </a>
                  <button
                    id="corvette-ppf-hero-quote"
                    onClick={goToQuote}
                    className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-lg font-bold transition w-full sm:w-auto"
                  >
                    <Calendar size={18} />
                    GET A FREE CORVETTE PPF QUOTE
                  </button>
                </div>
              </div>
              <div id="corvette-ppf-quote">
                <EmbeddedQuoteForm
                  variant="corvette"
                  defaultService="full-front-ppf"
                  serviceOptions={corvetteServiceOptions}
                  heading="Get a Free Corvette PPF Quote"
                  subheading="Name, phone, email, year, and Corvette model. Phone is the fastest way to reach us: 858-282-8119."
                />
              </div>
            </div>
          </div>
        </section>

        <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
              <li className="text-gray-600">›</li>
              <li><a href="/paint-protection-film-san-diego" className="hover:text-cyan-400 transition">Paint Protection Film San Diego</a></li>
              <li className="text-gray-600">›</li>
              <li className="text-cyan-400 font-medium" aria-current="page">Corvette PPF San Diego</li>
            </ol>
          </div>
        </nav>

        <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <Award className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">13 Years Experience</p>
                  <p className="text-gray-400 text-xs">Professional installation</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Vista Shop</p>
                  <p className="text-gray-400 text-xs">North County San Diego</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Premium Films</p>
                  <p className="text-gray-400 text-xs">Manufacturer-backed terms</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Car className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">C8 · Z06 · C7</p>
                  <p className="text-gray-400 text-xs">Quoted by model</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Why Corvettes Need PPF</h2>
            <div className="space-y-5 text-gray-300 text-lg">
              <p>
                A Corvette sits low and wide. The front bumper, hood, fenders, and rocker panels live closer to the road than most daily drivers, so they collect more grit from I-5, I-15, Highway 78, and canyon pavement. Rear valance and diffuser areas can also take debris depending on ride height and aero.
              </p>
              <p>
                Paint protection film does not stop every mark, and it is not a claim that Corvette paint is fragile. It is a clear (or colored) layer over the panels you want protected so chips and wash marring hit the film first. <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint protection film in San Diego</a> is installed here the same way we do other performance cars: wash, inspect, prep, then film selected for the panel.
              </p>
              <p>
                Mr Wrap installs Corvette PPF in Vista for owners across North County and greater San Diego County. If you are comparing shops and searching Corvette PPF near me, you are coming to a physical shop at 2244 South Santa Fe Avenue, Suite A7 — not a mobile-only setup.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">Corvette PPF Pricing</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
              Every Corvette is different. Pricing varies by generation, trim, aero package and selected coverage. Below are starting prices for Corvette PPF installations at Mr Wrap. Contact us with your year and model for an exact quote.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Front Bumper PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $799+</p>
                <p className="text-gray-300">The lowest, most exposed painted surface on a Corvette. A practical entry coverage if you want the nose protected without a full front kit.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Partial Front PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $1,499+</p>
                <p className="text-gray-300">Partial front coverage for Corvettes that need more than the bumper without a full front package. Exact panels are confirmed with your year and model.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Full Front PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $1,999+</p>
                <p className="text-gray-300">Corvette full front PPF typically covers the bumper, hood, fenders, and mirrors, with headlights added when they are a good candidate. This is the package most new C8 owners ask about first.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Track Package</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $2,999+</p>
                <p className="text-gray-300">Expanded protection for high-impact areas. Coverage is customized based on Corvette generation, trim and aero configuration.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Full Body Gloss PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $5,499+</p>
                <p className="text-gray-300">Corvette full body PPF in gloss film over painted surfaces. Best when you want more than the front protected and you plan to keep the factory color.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Matte / Stealth PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $6,499+</p>
                <p className="text-gray-300">Matte or stealth film for a satin look with the same type of impact protection. Use matching film on factory matte paint so the finish stays consistent.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-2">Colored PPF</h3>
                <p className="text-cyan-400 text-2xl font-bold mb-3">Starting at $6,999+</p>
                <p className="text-gray-300">Color-change film plus protection. See <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored PPF San Diego</a> for finish options. Complex Corvette bodywork may cost more than the starting price.</p>
              </div>
            </div>
            <p className="text-center text-gray-400 max-w-3xl mx-auto mt-8">
              Z06, Z07, exposed-carbon aero, aftermarket aero and other complex configurations may require custom pricing.
            </p>
            <div className="text-center mt-8">
              <button onClick={goToQuote} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center gap-2">
                <Calendar size={20} />
                GET MY CORVETTE PPF QUOTE
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">Real Corvette PPF Installation at Mr Wrap</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-10">
              These photos show paint protection film being professionally installed on a real orange C8 Corvette at our Vista shop. From detailed front-end installation to the finished result, this is the same hands-on Corvette protection work our customers trust us to perform.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <figure className="bg-gray-950 rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/4b/4b40aacb317cb47a5e3c7fc71a0d8a1b.webp"
                    alt="PPF installation on orange C8 Corvette at Mr Wrap in Vista CA"
                    width={1200}
                    height={900}
                    sizes="(max-width: 768px) 100vw, 640px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-4 text-sm text-gray-400">
                  Real C8 Corvette PPF Installation — paint protection film being professionally installed on this orange C8 Corvette at Mr Wrap in Vista.
                </figcaption>
              </figure>
              <figure className="bg-gray-950 rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/04/0412263763d62fd6c233a0201d669799.webp"
                    alt="C8 Corvette front end paint protection film installation at Mr Wrap"
                    width={1200}
                    height={900}
                    sizes="(max-width: 768px) 100vw, 640px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-4 text-sm text-gray-400">
                  Front-end paint protection film installation on the same orange C8 Corvette at Mr Wrap.
                </figcaption>
              </figure>
            </div>
            <figure className="bg-gray-950 rounded-xl border border-cyan-500/20 overflow-hidden max-w-4xl mx-auto">
              <div className="aspect-[16/10] bg-gray-900">
                <img
                  src="https://static.lumi.new/a5/a59dad50719f144b460b014e6f8ba543.webp"
                  alt="Orange C8 Corvette finished at Mr Wrap in Vista California"
                  width={1600}
                  height={1000}
                  sizes="(max-width: 768px) 100vw, 896px"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <figcaption className="p-4 text-sm text-gray-400 text-center">
                Finished C8 Corvette after PPF installation at Mr Wrap.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="py-16 bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">C8 Corvette PPF San Diego</h2>
                <div className="space-y-5 text-gray-300 text-lg">
                  <p>
                    C8 Corvette PPF San Diego work at Mr Wrap is quoted by trim, not by a generic sports-car kit. Mid-engine C8s share a low nose and wide hips, but Stingray, Z06, and E-Ray are not identical cars.
                  </p>
                  <p>
                    <strong className="text-white">Stingray:</strong> Most owners start with full front coverage. If the car is a highway car or sees canyon miles, we often talk through rockers and other high-impact pieces as an add.
                  </p>
                  <p>
                    <strong className="text-white">Z06:</strong> Wider bodywork and a more aggressive front end usually mean more film and more time. C8 PPF San Diego quotes for Z06 are separate from Stingray. Details are in the Z06 section below.
                  </p>
                  <p>
                    <strong className="text-white">E-Ray:</strong> All-wheel-drive packaging and unique aero still need the same prep and edge work. We inspect the specific car rather than assuming Stingray coverage will transfer.
                  </p>
                </div>
              </div>
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/12/12c7a3a290e4b6d39f0fe41049a0b57d.webp"
                    alt="Blue C8 Corvette at Mr Wrap in Vista California"
                    width={1200}
                    height={900}
                    sizes="(max-width: 1024px) 100vw, 560px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-4 text-sm text-gray-400">
                  C8 Corvette — PPF + Ceramic Tint. Real C8 Corvette protected with PPF and ceramic window tint at Mr Wrap in Vista.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Corvette Z06 PPF San Diego</h2>
            <div className="space-y-5 text-gray-300 text-lg">
              <p>
                Corvette Z06 PPF San Diego installs are planned around the wider body, the front end, and the rocker/impact areas that see tire spray and debris. Z06 paint protection film is not a Stingray kit stretched to fit.
              </p>
              <p>
                Carbon and aero pieces vary by car. Some Z06s have more exposed carbon, splitters, or rocker extensions than others. We protect those surfaces only when the material and finish are a good candidate for film. We will not claim every Z06 leaves the factory with the same aero package.
              </p>
              <p>
                If you want Z06 coverage plus <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> or a <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">vehicle wrap</a> accent, we can plan that in one Vista visit when the schedule allows.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">More Real Corvettes at Mr Wrap</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
              Real customer Corvettes completed at our Vista shop — PPF, ceramic tint, and related protection work we perform every week.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/a5/a59adf21bddbd801fb4b20affbb71d54.webp"
                    alt="Orange C8 Corvette inside Mr Wrap Vista shop"
                    width={1200}
                    height={900}
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2">Real C8 Corvette PPF Installation</h3>
                  <p className="text-gray-400 text-sm">The same orange C8 Corvette PPF project inside Mr Wrap in Vista.</p>
                </figcaption>
              </figure>
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/75/7568fb7cd0ce635b4dd2a7d0b87af940.webp"
                    alt="Red C7 Corvette outside Mr Wrap in Vista California"
                    width={1200}
                    height={900}
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2">C7 Corvette — PPF, Paint Correction & Ceramic Tint</h3>
                  <p className="text-gray-400 text-sm">Mr Wrap protected this C7 with PPF across the front end, doors and trunk, along with paint correction and ceramic window tint.</p>
                </figcaption>
              </figure>
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-900">
                  <img
                    src="https://static.lumi.new/16/16f1b02c91fc9c29cf3cd5e536fa2e86.webp"
                    alt="Yellow Corvette at Mr Wrap in Vista California"
                    width={1200}
                    height={900}
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2">Yellow Corvette — Ceramic Tint & Wheel Spacers</h3>
                  <p className="text-gray-400 text-sm">Ceramic window tint and wheel spacers completed at Mr Wrap in Vista.</p>
                </figcaption>
              </figure>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:858-282-8119"
                className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-4 rounded-lg font-bold transition"
              >
                <Phone size={18} />
                Call / Text 858-282-8119
              </a>
              <button
                onClick={goToQuote}
                className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-lg font-bold transition"
              >
                <Calendar size={18} />
                Get Quote
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-white">Why Corvette Owners Choose Mr Wrap</h2>
            <ul className="space-y-4 text-gray-300 text-lg">
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>13 years of professional installation experience</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>Vista shop serving North County and San Diego County</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>Premium professional-grade films with manufacturer warranty terms confirmed on the quote</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>Careful prep: wash, decontaminate, and inspect before film goes on</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>Wrapped or tucked edges where appropriate — not every edge on every panel</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>Bulk or precision-pattern installation depending on the vehicle and panel</span></li>
              <li className="flex gap-3"><Check className="text-cyan-400 flex-shrink-0 mt-1" size={22} /><span>PPF, wraps, and ceramic window tint available in one shop</span></li>
            </ul>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">How much does PPF cost for a Corvette in San Diego?</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-10">
              Corvette PPF cost in San Diego depends on model, coverage, film, aero or carbon components, paint condition, and installation complexity. A C8 Stingray full front is not priced the same as a Z06 full body. These are starting prices for Corvette PPF at Mr Wrap, not a guaranteed lock-in.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[
                { name: "Front Bumper", price: "Starting at $799+" },
                { name: "Partial Front", price: "Starting at $1,499+" },
                { name: "Full Front", price: "Starting at $1,999+" },
                { name: "Track Package", price: "Starting at $2,999+" },
                { name: "Full Body Gloss", price: "Starting at $5,499+" },
                { name: "Matte / Stealth", price: "Starting at $6,499+" },
                { name: "Colored PPF", price: "Starting at $6,999+" }
              ].map((tier) => (
                <div key={tier.name} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                  <div className="text-cyan-400 text-2xl font-bold">{tier.price}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 max-w-3xl mx-auto mb-8">
              See full coverage notes on <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint Protection Film San Diego</a>. Starting prices are not a Corvette lock-in.
            </p>
            <div className="text-center">
              <button onClick={goToQuote} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center gap-2">
                <Calendar size={20} />
                GET MY CORVETTE PPF QUOTE
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-white text-center">Corvette PPF FAQ</h2>
            <div className="space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold mb-3 text-white">{faq.question}</h3>
                  <p className="text-gray-300">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmbeddedQuoteForm
              variant="corvette"
              defaultService="full-front-ppf"
              serviceOptions={corvetteServiceOptions}
              heading="Request a Corvette PPF Quote"
              subheading="Share year, model, and coverage. Prefer the phone? Call or text 858-282-8119."
            />
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
                <p className="text-gray-400 text-sm">Main PPF coverage options and starting prices</p>
              </a>
              <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
                <p className="text-gray-400 text-sm">Color-change film when you want a new finish plus protection</p>
              </a>
              <a href="/ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">PPF San Diego</h3>
                <p className="text-gray-400 text-sm">County-wide PPF overview from the Vista shop</p>
              </a>
              <a href="/window-tint-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Ceramic Window Tint</h3>
                <p className="text-gray-400 text-sm">Tint that can be planned with Corvette PPF in one shop</p>
              </a>
              <a href="/car-wrap-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Vehicle Wraps</h3>
                <p className="text-gray-400 text-sm">Vinyl color-change wraps when film is not the goal</p>
              </a>
              <a href="/contact" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Contact / Quote</h3>
                <p className="text-gray-400 text-sm">Call, text, or send a quote from the contact page</p>
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white flex items-center justify-center gap-3">
              <MapPin className="text-cyan-400" size={28} />
              Vista shop for North County and San Diego County
            </h2>
            <p className="text-gray-300 text-lg mb-4">
              Mr Wrap · 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084 · 858-282-8119 · autowrapsandiego.com
            </p>
            <p className="text-gray-400 mb-4">
              We install at the Vista shop. Corvette owners typically come from Vista, Oceanside, Carlsbad, San Marcos, Encinitas, Rancho Santa Fe, North County San Diego, and greater San Diego County. We do not claim a customer list by city.
            </p>
            <p className="text-gray-400">
              <Clock className="inline mr-2 mb-1" size={16} />
              Monday–Friday 8:00 AM–6:00 PM · Saturday 9:00 AM–4:00 PM
            </p>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Get a Free Corvette PPF Quote</h2>
            <p className="text-xl text-white/90 mb-8">
              Phone first if you want a fast answer. Call or text 858-282-8119, or send the form with year and model.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call 858-282-8119
              </a>
              <a href="sms:858-282-8119" className="bg-black/20 hover:bg-black/30 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2 border border-white/40">
                <MessageCircle size={20} />
                Text Us
              </a>
              <button onClick={goToQuote} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                GET MY CORVETTE PPF QUOTE
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-black/95 border-t border-cyan-500/30 px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <div className="grid grid-cols-2 gap-2">
          <a href="tel:858-282-8119" className="flex items-center justify-center gap-1 bg-cyan-500 text-white font-bold py-3 rounded-lg text-xs sm:text-sm">
            <Phone size={16} />
            858-282-8119
          </a>
          <button onClick={goToQuote} className="flex items-center justify-center gap-1 bg-orange-500 text-white font-bold py-3 rounded-lg text-xs sm:text-sm">
            Get Quote
          </button>
        </div>
      </div>
    </>
  )
}

export default CorvettePpfSanDiegoPage
